import { ArrowDropDown, OpenInNew } from '@mui/icons-material';
import {
    Button,
    ButtonGroup,
    ClickAwayListener,
    FormControl,
    Grow, ListSubheader, MenuItem,
    MenuList,
    Paper,
    Popper,
    styled,
    Tooltip,
} from '@mui/material';
import { useRef, useState } from 'react';
import { config } from 'src/lib/config';
import { GroupedLocales } from 'src/components/ForcedRankingDetails/RankingTabs';

interface Props {
    ariaLabel: string;
    cultures: string[];
    disabled: boolean;
    label: string;
    selectedCulture: string;
    tooltip: string;
    onClick: () => void;
    onSelectCulture: (culture: string) => void;
}

const StyledPopper = styled(Popper)(() => ({
    width: 'fit-content',
    zIndex: 10001,
}));

const StyledMenuList = styled(MenuList)(({ theme }) => ({
    width: 'fit-content',
    minWidth: theme.spacing(30),
}));

const StyledButton = styled(Button)(({ theme }) => ({
    '&.Mui-disabled': {
        pointerEvents: 'auto',
    },
    '&.Mui-disabled:hover': {
        backgroundColor: theme.palette.common.white,
    },
}));

export const CultureSplitButton = ({
    ariaLabel,
    cultures,
    disabled,
    label,
    selectedCulture,
    tooltip,
    onClick,
    onSelectCulture,
}: Props): JSX.Element => {
    const [open, setOpen] = useState(false);
    const anchorRef = useRef<HTMLDivElement>(null);
    const { groupedLocales } = config as unknown as { groupedLocales: GroupedLocales };

    const handleClick = (): void => {
        if (!disabled) {
            onClick();
        }
    };

    const handleToggle = (): void => {
        setOpen((prev) => !prev);
    };

    const handleClose = (event: Event): void => {
        if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
            return;
        }

        setOpen(false);
    };

    return (
        <FormControl size="small">
            <ButtonGroup
                aria-label={ariaLabel}
                ref={anchorRef}
                variant="outlined"
            >
                <Tooltip title={tooltip}>
                    <StyledButton
                        disabled={disabled}
                        startIcon={<OpenInNew />}
                        onClick={handleClick}
                    >
                        {disabled ? label : `${label} (${selectedCulture})`}
                    </StyledButton>
                </Tooltip>
                <Tooltip title="Select culture">
                    <Button
                        disabled={disabled}
                        size="small"
                        onClick={handleToggle}
                    >
                        <ArrowDropDown />
                    </Button>
                </Tooltip>
            </ButtonGroup>
            <StyledPopper
                disablePortal
                transition
                anchorEl={anchorRef.current}
                open={open}
                placement="bottom-end"
            >
                {({ TransitionProps, placement }): JSX.Element => (
                    <Grow
                        {...TransitionProps}
                        style={{
                            transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
                        }}
                    >
                        <Paper>
                            <ClickAwayListener onClickAway={handleClose}>
                                <StyledMenuList id="split-button-menu">
                                    {Object.entries(groupedLocales)
                                        .filter(([, locales]) => locales.some((locale) => cultures.includes(locale)))
                                        .map(([region, locales]) => (
                                            [
                                                <ListSubheader key={region}>{region}</ListSubheader>,
                                                ...locales
                                                    .filter((locale) => cultures.includes(locale))
                                                    .flatMap((culture) => (
                                                        <MenuItem
                                                            key={culture}
                                                            selected={culture === selectedCulture}
                                                            onClick={(): void => {
                                                                onSelectCulture(culture);
                                                                setOpen(false);
                                                            }}
                                                        >
                                                            {culture}
                                                        </MenuItem>
                                                    )),
                                            ]
                                        ))}
                                </StyledMenuList>
                            </ClickAwayListener>
                        </Paper>
                    </Grow>
                )}
            </StyledPopper>
        </FormControl>
    );
};
